import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import { api } from "../../lib/api";
import { ageFromBirth } from "../../lib/calc";
import { Syringe, Loader2, Sparkles, ShieldCheck, Info } from "lucide-react";
import { toast } from "sonner";

export default function VaccinesPanel({ patient }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const ageM = ageFromBirth(patient.birth_date).months;

  const load = useCallback(async () => {
    const ck = `clinped_vx_${ageM}`;
    const cached = localStorage.getItem(ck);
    if (cached) { try { setData(JSON.parse(cached)); return; } catch { /* ignore */ } }
    setLoading(true);
    try {
      const res = await api.post("/ai/assist", { patient_id: patient.patient_id, type: "vaccines", age_months: ageM });
      setData(res.data.parsed);
      if (res.data.parsed) localStorage.setItem(ck, JSON.stringify(res.data.parsed));
      else toast.error("La IA no devolvió datos");
    } catch (e) { toast.error("Error de IA"); }
    finally { setLoading(false); }
  }, [patient.patient_id, ageM]);

  return (
    <div className="space-y-5">
      <div className="bg-white rounded-3xl p-6 border border-stone-100 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand-terracotta/10 flex items-center justify-center text-brand-terracotta"><Syringe size={24} /></div>
          <div>
            <h3 className="font-outfit font-bold text-brand-ink">Vacunas recomendadas</h3>
            <p className="text-sm text-muted-foreground">Incluye vacunas fuera del esquema gratuito · {ageFromBirth(patient.birth_date).text}</p>
          </div>
        </div>
        <button data-testid="load-vaccines-btn" onClick={load} disabled={loading}
          className="flex items-center gap-2 bg-brand-terracotta hover:bg-brand-terracottaDark text-white font-semibold px-4 py-2.5 rounded-xl text-sm transition-all hover:scale-[1.02] disabled:opacity-60">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />} Recomendar con IA
        </button>
      </div>

      {data && (
        <div className="space-y-5" data-testid="vaccines-result">
          <div>
            <p className="text-xs font-bold uppercase tracking-wide text-brand-terracotta mb-3">No incluidas en el esquema gratuito</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {data.recomendadas_no_gratuitas?.map((v, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-3xl p-5 border border-stone-100 shadow-[0_8px_30px_rgb(61,64,91,0.04)]">
                  <div className="flex items-center gap-2 mb-1">
                    <Syringe size={16} className="text-brand-terracotta" />
                    <p className="font-outfit font-bold text-brand-ink">{v.vacuna}</p>
                  </div>
                  {v.esquema && <p className="text-xs font-semibold text-brand-sageDark bg-brand-sage/10 inline-block px-2 py-0.5 rounded-full mb-1.5">{v.esquema}</p>}
                  <p className="text-sm text-muted-foreground">{v.motivo}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {data.esquema_gratuito_actual?.length > 0 && (
            <div className="bg-brand-sage/5 rounded-3xl p-5 border border-brand-sage/10">
              <p className="font-outfit font-bold text-brand-sageDark flex items-center gap-2 mb-2"><ShieldCheck size={18} /> Esquema gratuito que corresponde ahora</p>
              <ul className="grid sm:grid-cols-2 gap-1.5">
                {data.esquema_gratuito_actual.map((s, i) => <li key={i} className="text-sm text-brand-ink/80 flex gap-2"><span className="text-brand-sage mt-1">•</span>{s}</li>)}
              </ul>
            </div>
          )}

          {data.nota && (
            <div className="flex gap-2 text-xs text-muted-foreground bg-muted rounded-2xl p-3">
              <Info size={14} className="shrink-0 mt-0.5" /> {data.nota}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
